import { useState } from "react";
import { Button, Typography } from "@mui/material";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import {
  useRemoveGroupMemberMutation,
  useRemoveGroupManagerMutation,
} from "../../../api/managementApi";
import { useToast } from "../../../components/ui/Toast";
import RemovePersonConfirm from "./RemovePersonConfirm";
import UserAvatar from "../../../components/ui/UserAvatar";

export default function PersonList({ groupId, role, people, currentUserId, emptyText, onEditGoals }) {
  const { t } = useScriptMode();
  const toast = useToast();
  const [removeMember, { isLoading: removingMember }] = useRemoveGroupMemberMutation();
  const [removeManager, { isLoading: removingManager }] = useRemoveGroupManagerMutation();

  const [pendingRemoval, setPendingRemoval] = useState(null);
  const isLoading = removingMember || removingManager;

  if (!Array.isArray(people) || people.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyText}
      </Typography>
    );
  }

  const handleConfirm = async () => {
    if (!pendingRemoval) return;
    const fn = role === "manager" ? removeManager : removeMember;
    try {
      await fn({ groupId, email: pendingRemoval.email }).unwrap();
      toast.success(
        role === "manager" ? t("manage_manager_removed") : t("manage_member_removed")
      );
    } catch (err) {
      const msg = err?.message || err?.data?.message || t("manage_remove_error");
      toast.error(msg);
    } finally {
      setPendingRemoval(null);
    }
  };

  return (
    <>
      <ul className="flex flex-col gap-2.5">
        {people.map((p) => {
          const isSelf = String(p.id) === String(currentUserId);
          return (
            <li
              key={p.id}
              className="flex items-center gap-3 p-3 rounded-xl border border-terakkiBorder hover:border-terakkiPrimary hover:bg-terakkiSurfaceAlt transition-colors dark:border-white/[0.14] dark:hover:border-white/30 dark:hover:bg-white/5"
            >
              <UserAvatar person={p} size={44} />
              <div className="flex-1 min-w-0">
                <div className="font-SemiBold text-base truncate">
                  {p.name} {p.surname} {isSelf ? `(${t("manage_self_label")})` : ""}
                </div>
                <div className="text-sm text-terakkiNeutral truncate dark:text-white/70">{p.email}</div>
              </div>
              {role === "member" && onEditGoals ? (
                <Button
                  variant="outlined"
                  startIcon={<FlagOutlinedIcon />}
                  onClick={() => onEditGoals(p)}
                >
                  {t("manage_goals_cta")}
                </Button>
              ) : null}
              {isSelf ? (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {t("manage_self_cant_remove")}
                </Typography>
              ) : (
                <Button
                  color="error"
                  variant="outlined"
                  startIcon={<PersonRemoveOutlinedIcon />}
                  onClick={() => setPendingRemoval(p)}
                >
                  {t("manage_remove_person_cta")}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
      <RemovePersonConfirm
        open={!!pendingRemoval}
        loading={isLoading}
        onClose={() => setPendingRemoval(null)}
        onConfirm={handleConfirm}
      />
    </>
  );
}
